/**
 * AI Orchestration Center API Service
 * Handles all AI task monitoring and management API calls
 */

import axios from 'axios';
import { config } from '@/src/config';

const API_URL = config.weaveinApi.baseUrl;

// Helper to get auth headers
const getHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');

  return {
    'Authorization': `Bearer ${token}`,
    'X-CBO-ID': cboId
  };
};

// Type definitions
export type AITaskCategory =
  | 'external_integration'
  | 'voice_ai'
  | 'data_enrichment'
  | 'communication_ai'
  | 'predictive_analytics'
  | 'document_processing'
  | 'workflow_automation'
  | 'quality_assurance';

export type AITaskStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' | 'retry';
export type AIProvider = 'anthropic_claude' | 'openai_gpt' | 'google_gemini' | 'assembly_ai' | 'deepgram' | 'custom_model' | 'internal';

export interface AITaskSummary {
  id: number;
  task_category: AITaskCategory;
  task_type: string;
  task_name: string;
  ai_provider: AIProvider;
  model_name?: string;
  status: AITaskStatus;
  priority: number;
  queued_at: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  total_tokens?: number;
  actual_cost_usd?: number;
  related_entity_type?: string;
  related_entity_id?: number;
}

export interface AITaskDetail extends AITaskSummary {
  cbo_id: number;
  task_description?: string;
  model_version?: string;
  parent_task_id?: number;
  input_data?: Record<string, any>;
  output_data?: Record<string, any>;
  input_tokens?: number;
  output_tokens?: number;
  estimated_cost_usd?: number;
  confidence_score?: number;
  quality_score?: number;
  validation_passed?: boolean;
  validation_errors?: Record<string, any>;
  error_message?: string;
  error_code?: string;
  retry_count: number;
  max_retries: number;
  execution_mode: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface AIProviderMetrics {
  ai_provider: AIProvider;
  model_name?: string;
  metric_date: string;
  total_tasks: number;
  successful_tasks: number;
  failed_tasks: number;
  total_tokens: number;
  total_cost_usd: number;
  avg_duration_ms?: number;
  avg_confidence_score?: number;
}

export interface AIBudgetStatus {
  cbo_id: number;
  monthly_budget_usd?: number;
  daily_budget_usd?: number;
  current_month_spend: number;
  current_day_spend: number;
  monthly_token_limit?: number;
  current_month_tokens: number;
  budget_exceeded: boolean;
  max_concurrent_tasks: number;
  alert_threshold_percentage: number;
}

export interface DashboardStats {
  total_tasks_today: number;
  running_tasks: number;
  queued_tasks: number;
  completed_today: number;
  failed_today: number;
  total_cost_today: number;
  total_tokens_today: number;
  avg_duration_ms?: number;
  avg_confidence_score?: number;
  budget_utilization_percent?: number;
}

export interface QualityFeedback {
  ai_task_id: number;
  rating: number;
  is_accurate?: boolean;
  is_helpful?: boolean;
  feedback_text?: string;
  expected_output?: Record<string, any>;
}

export interface TaskLog {
  log_level: string;
  log_message: string;
  log_data?: Record<string, any>;
  timestamp: string;
}

// Performance & Quality Framework Types

export interface AIBenchmark {
  id: number;
  cbo_id?: string;
  task_category: AITaskCategory;
  task_type: string;
  metric_name: string;
  target_value: number;
  warning_threshold: number;
  critical_threshold: number;
  comparison_operator: 'lt' | 'lte' | 'gt' | 'gte' | 'eq';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AITaskOutcome {
  id: number;
  ai_task_id: number;
  cbo_id: string;
  task_category: AITaskCategory;
  task_type: string;
  outcome_status: 'success' | 'partial_success' | 'failure' | 'timeout' | 'cancelled';
  outcome_details?: Record<string, any>;
  business_impact?: string;
  user_satisfaction_score?: number;
  benchmark_results?: Record<string, any>;
  benchmarks_passed: number;
  benchmarks_failed: number;
  benchmarks_warning: number;
  created_at: string;
}

export interface AIWorkflow {
  id: number;
  cbo_id: string;
  workflow_name: string;
  workflow_type?: string;
  trigger_type: string;
  trigger_entity_type?: string;
  trigger_entity_id?: number;
  status: 'active' | 'completed' | 'failed' | 'cancelled';
  started_at: string;
  completed_at?: string;
  total_steps: number;
  completed_steps: number;
  failed_steps: number;
  total_duration_ms?: number;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface AIWorkflowStep {
  id: number;
  workflow_id: number;
  ai_task_id?: number;
  step_number: number;
  step_name: string;
  step_type?: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  input_data?: Record<string, any>;
  output_data?: Record<string, any>;
  error_message?: string;
  created_at: string;
}

export interface DailyPerformance {
  task_category: AITaskCategory;
  task_type: string;
  performance_date: string;
  total_tasks: number;
  successful_tasks: number;
  failed_tasks: number;
  avg_duration_ms: number;
  p50_duration_ms: number;
  p95_duration_ms: number;
  avg_cost_usd: number;
  total_cost_usd: number;
  avg_confidence_score?: number;
  avg_quality_score?: number;
  success_rate: number;
}

export interface LeakageSummary {
  workflow_name: string;
  workflow_type?: string;
  total_workflows: number;
  completed_workflows: number;
  leaked_workflows: number;
  leakage_rate: number;
  avg_completion_rate: number;
  most_common_failure_step?: string;
}

export interface WorkflowStepDropoff {
  workflow_name: string;
  step_number: number;
  step_name: string;
  started_count: number;
  completed_count: number;
  failed_count: number;
  dropoff_rate: number;
  avg_duration_ms?: number;
}

// Helper functions
export const getCategoryLabel = (category: AITaskCategory): string => {
  const labels: Record<AITaskCategory, string> = {
    external_integration: 'External Integration',
    voice_ai: 'Voice AI',
    data_enrichment: 'Data Enrichment',
    communication_ai: 'Communication AI',
    predictive_analytics: 'Predictive Analytics',
    document_processing: 'Document Processing',
    workflow_automation: 'Workflow Automation',
    quality_assurance: 'Quality Assurance'
  };
  return labels[category] || category;
};

export const getProviderLabel = (provider: AIProvider): string => {
  const labels: Record<AIProvider, string> = {
    anthropic_claude: 'Anthropic Claude',
    openai_gpt: 'OpenAI GPT',
    google_gemini: 'Google Gemini',
    assembly_ai: 'AssemblyAI',
    deepgram: 'Deepgram',
    custom_model: 'Custom Model',
    internal: 'Internal'
  };
  return labels[provider] || provider;
};

export const getStatusColor = (status: AITaskStatus): string => {
  const colors: Record<AITaskStatus, string> = {
    queued: 'gray',
    running: 'blue',
    completed: 'green',
    failed: 'red',
    cancelled: 'gray',
    retry: 'yellow'
  };
  return colors[status] || 'gray';
};

// API Functions

export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/dashboard`, {
    headers: getHeaders()
  });
  return response.data;
};

export const listAITasks = async (filters?: {
  task_category?: AITaskCategory;
  status?: AITaskStatus;
  ai_provider?: AIProvider;
  limit?: number;
}): Promise<AITaskSummary[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/tasks`, {
    headers: getHeaders(),
    params: filters
  });
  return response.data;
};

export const getAITask = async (taskId: number): Promise<AITaskDetail> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/tasks/${taskId}`, {
    headers: getHeaders()
  });
  return response.data;
};

export const getProviderMetrics = async (days: number = 7): Promise<AIProviderMetrics[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/providers/metrics`, {
    headers: getHeaders(),
    params: { days }
  });
  return response.data;
};

export const getBudgetStatus = async (): Promise<AIBudgetStatus> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/budget`, {
    headers: getHeaders()
  });
  return response.data;
};

export const submitQualityFeedback = async (feedback: QualityFeedback): Promise<{ id: number; message: string }> => {
  const response = await axios.post(
    `${API_URL}/api/ai-orchestration/tasks/${feedback.ai_task_id}/feedback`,
    feedback,
    { headers: getHeaders() }
  );
  return response.data;
};

export const getTaskLogs = async (taskId: number): Promise<TaskLog[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/tasks/${taskId}/logs`, {
    headers: getHeaders()
  });
  return response.data;
};

// Performance & Quality Framework API Functions

export const getBenchmarks = async (filters?: {
  task_category?: AITaskCategory;
  task_type?: string;
  is_active?: boolean;
}): Promise<AIBenchmark[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/benchmarks`, {
    headers: getHeaders(),
    params: filters
  });
  return response.data;
};

export const checkTaskBenchmark = async (taskId: number): Promise<{
  task_id: number;
  benchmark_results: Record<string, any>;
  passed: number;
  failed: number;
  warning: number;
}> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/tasks/${taskId}/benchmark`, {
    headers: getHeaders()
  });
  return response.data;
};

export const getDailyPerformance = async (filters?: {
  task_category?: AITaskCategory;
  task_type?: string;
  days?: number;
}): Promise<DailyPerformance[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/performance/daily`, {
    headers: getHeaders(),
    params: filters
  });
  return response.data;
};

export const getWorkflows = async (filters?: {
  status?: 'active' | 'completed' | 'failed' | 'cancelled';
  workflow_name?: string;
  limit?: number;
}): Promise<AIWorkflow[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/workflows`, {
    headers: getHeaders(),
    params: filters
  });
  return response.data;
};

export const getWorkflowSteps = async (workflowId: number): Promise<AIWorkflowStep[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/workflows/${workflowId}/steps`, {
    headers: getHeaders()
  });
  return response.data;
};

export const getLeakageSummary = async (days?: number): Promise<LeakageSummary[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/leakage-summary`, {
    headers: getHeaders(),
    params: { days: days || 30 }
  });
  return response.data;
};

export const getTaskOutcomes = async (filters?: {
  task_category?: AITaskCategory;
  outcome_status?: string;
  limit?: number;
}): Promise<AITaskOutcome[]> => {
  const response = await axios.get(`${API_URL}/api/ai-orchestration/outcomes`, {
    headers: getHeaders(),
    params: filters
  });
  return response.data;
};

export const recordTaskOutcome = async (outcome: {
  ai_task_id: number;
  outcome_status: string;
  outcome_details?: Record<string, any>;
  business_impact?: string;
  user_satisfaction_score?: number;
}): Promise<{ id: number; message: string }> => {
  const response = await axios.post(
    `${API_URL}/api/ai-orchestration/outcomes`,
    outcome,
    { headers: getHeaders() }
  );
  return response.data;
};

export const recordQualityScore = async (score: {
  ai_task_id: number;
  score_type: string;
  score_value: number;
  score_details?: Record<string, any>;
  evaluated_by?: string;
}): Promise<{ id: number; message: string }> => {
  const response = await axios.post(
    `${API_URL}/api/ai-orchestration/quality-scores`,
    score,
    { headers: getHeaders() }
  );
  return response.data;
};

// ============================================================================
// Employer Agentic Workflow Integration
// ============================================================================

export interface EmployerAgenticWorkflow {
  id: number;
  workflow_instance_id: number;
  employer_id: number;
  employer_name: string;
  employee_id: number;
  employee_name: string;
  template_name: string;
  template_type: string;
  ai_model_config?: {
    primary_model: string;
    decision_model: string;
    communication_model: string;
    model_parameters?: Record<string, any>;
  };
  status: string;
  ai_models_used?: string[];
  total_ai_tokens_used?: number;
  ai_decisions_count?: number;
  initiated_at: string;
  completed_at?: string;
  duration_seconds?: number;
  outcome?: string;
  referral_created?: boolean;
}

/**
 * Get employer agentic workflows for AI Orchestration Center tracking
 */
export const getEmployerAgenticWorkflows = async (filters?: {
  employer_id?: number;
  status?: string;
  template_type?: string;
  limit?: number;
}): Promise<EmployerAgenticWorkflow[]> => {
  try {
    const response = await axios.get(`${API_URL}/api/employer-workflows/ai-tracking`, {
      headers: getHeaders(),
      params: filters
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching employer agentic workflows:', error);
    return [];
  }
};

/**
 * Convert employer workflows to AI Orchestration format
 */
export const convertToAIWorkflow = (workflow: EmployerAgenticWorkflow): AIWorkflow => {
  const statusMap: Record<string, 'active' | 'completed' | 'failed' | 'cancelled'> = {
    'pending': 'active',
    'queued': 'active',
    'in_progress': 'active',
    'waiting_response': 'active',
    'completed': 'completed',
    'cancelled': 'cancelled',
    'escalated': 'failed',
    'paused': 'active',
  };

  return {
    id: workflow.workflow_instance_id,
    cbo_id: '',
    workflow_name: `${workflow.template_name} - ${workflow.employee_name}`,
    workflow_type: `employer_${workflow.template_type}`,
    status: statusMap[workflow.status] || 'active',
    trigger_type: 'manual',
    total_steps: 5, // Default for employer workflows
    completed_steps: workflow.status === 'completed' ? 5 : workflow.status === 'in_progress' ? 2 : 0,
    failed_steps: workflow.status === 'escalated' ? 1 : 0,
    total_tokens_used: workflow.total_ai_tokens_used,
    total_cost_usd: (workflow.total_ai_tokens_used || 0) * 0.00001, // Estimate cost
    total_duration_ms: (workflow.duration_seconds || 0) * 1000,
    started_at: workflow.initiated_at,
    completed_at: workflow.completed_at,
    created_at: workflow.initiated_at,
    updated_at: workflow.completed_at || workflow.initiated_at,
  };
};
